const COPIED_BLOCK_KEY = 'temp.copiedBlock'

class BlockLabelsCopyService {
  get copiedBlock () {
    if (this.copiedBlockCache) {
      return this.copiedBlockCache
    } else {
      const block = window.localStorage.getItem(COPIED_BLOCK_KEY)
      try {
        return JSON.parse(block)
      } catch (error) {
        console.error(error)
      }
    }
  }

  set copiedBlock (block) {
    const blockJson = JSON.stringify(block)
    window.localStorage.setItem(COPIED_BLOCK_KEY, blockJson)
    this.copiedBlockCache = block
  }

  hasCopiedBlock () {
    return !!this.copiedBlock
  }

  copy (block) {
    this.copiedBlock = block
  }

  pasteTo (block) {
    if (!this.copiedBlock) {
      return
    }

    block.props = block.props || {}

    // copy over subgroup labels
    if (Array.isArray(block.subgroups) && Array.isArray(this.copiedBlock.subgroups)) {
      this.copyOverLabels(this.copiedBlock.subgroups, block.subgroups)

      // make a new array so that
      block.subgroups = [...block.subgroups]
    }

    // copy over filter option labels
    if (block.props.filterDefinition && Array.isArray(block.props.filterDefinition.fields)) {
      const copiedFilterDefinition = this.copiedBlock.props && this.copiedBlock.props.filterDefinition && this.copiedBlock.props.filterDefinition.fields
      this.copyOverNestedLabels(copiedFilterDefinition, block.props.filterDefinition.fields, 'options', 'key', block.subgroups)
    }

    // copy over strata labels
    if (block.props.strataDefinition && Array.isArray(block.props.strataDefinition.options)) {
      const copiedStrataDefinition = this.copiedBlock.props && this.copiedBlock.props.strataDefinition && this.copiedBlock.props.strataDefinition.options
      this.copyOverNestedLabels(copiedStrataDefinition, block.props.strataDefinition.options, 'legend', 'value', block.subgroups)
    }

    // copy over slider definition
    if (block.props.sliderDefinition && this.copiedBlock.props && this.copiedBlock.props.sliderDefinition &&
      (block.props.sliderDefinition.key === this.copiedBlock.props.sliderDefinition.key)) {
      block.props.sliderDefinition.label = this.copiedBlock.props.sliderDefinition.label
      this.copyOverLabels(this.copiedBlock.props.sliderDefinition.options, block.props.sliderDefinition.options)
    }

    // copy over axis labels
    if ((block.type === 'line') || (block.type === 'bar')) {
      const copiedXAxisLabel = this.copiedBlock.props && this.copiedBlock.props.axisLabels && this.copiedBlock.props.axisLabels.x
      const copiedYAxisLabel = this.copiedBlock.props && this.copiedBlock.props.axisLabels && this.copiedBlock.props.axisLabels.y
      if (copiedXAxisLabel) {
        block.props.axisLabels = block.props.axisLabels || {}
        block.props.axisLabels.x = copiedXAxisLabel
      }
      if (copiedYAxisLabel) {
        block.props.axisLabels = block.props.axisLabels || {}
        block.props.axisLabels.y = copiedYAxisLabel
      }
    }

    // copy over column groups labels
    if (Array.isArray(block.props.columnGroups) && (block.props.columnGroups.length > 0) &&
      this.copiedBlock.props && Array.isArray(this.copiedBlock.props.columnGroups) && (this.copiedBlock.props.columnGroups.length > 0)) {
      this.copyOverNestedLabels(this.copiedBlock.props.columnGroups, block.props.columnGroups, 'columns', 'value', block.subgroups)
    }

    // copy over column labels
    if (Array.isArray(block.props.columns) && (block.props.columns.length > 0) &&
      this.copiedBlock.props && Array.isArray(this.copiedBlock.props.columns) && (this.copiedBlock.props.columns.length > 0)) {
      this.copyOverLabels(this.copiedBlock.props.columns, block.props.columns)
    }

    // copy over section labels
    if (Array.isArray(block.props.sections) && (block.props.sections.length > 0) &&
      this.copiedBlock.props && Array.isArray(this.copiedBlock.props.sections) && (this.copiedBlock.props.sections.length > 0)) {
      this.copyOverNestedLabels(this.copiedBlock.props.sections, block.props.sections, 'rows', 'value', block.subgroups)
    }

    // copy over row labels
    if (Array.isArray(block.props.rows) && (block.props.rows.length > 0) &&
      this.copiedBlock.props && Array.isArray(this.copiedBlock.props.rows) && (this.copiedBlock.props.rows.length > 0)) {
      this.copyOverLabels(this.copiedBlock.props.rows, block.props.rows)
    }

    // copy over table secondary label
    if (block.props.sparklineVariable && this.copiedBlock.props && this.copiedBlock.props.secondaryLabel &&
      (block.props.sparklineVariable === this.copiedBlock.props.sparklineVariable)) {
      block.props.secondaryLabel = this.copiedBlock.props.secondaryLabel
    }

    // copy over map and sankey value label
    if (((block.type === 'map') && (this.copiedBlock.type === 'map')) || ((block.type === 'sankey') && (this.copiedBlock.type === 'sankey'))) {
      const copiedValueLabel = this.copiedBlock.props && this.copiedBlock.props.valueLabel
      if (copiedValueLabel) {
        block.props.valueLabel = copiedValueLabel
      }
    }

    // copy over sankey state and stage labels
    if ((block.type === 'sankey') && (this.copiedBlock.type === 'sankey')) {
      const copiedStageLabels = this.copiedBlock.props && this.copiedBlock.props.stageLabels
      if (Array.isArray(block.props.stageLabels) && Array.isArray(copiedStageLabels) && (block.props.stageLabels.length === copiedStageLabels.length)) {
        block.props.stageLabels = copiedStageLabels
      }

      const copiedStateLabels = this.copiedBlock.props && this.copiedBlock.props.stateLabels
      if (Array.isArray(block.props.stateLabels) && Array.isArray(copiedStateLabels) && (block.props.stateLabels.length === copiedStateLabels.length)) {
        block.props.stateLabels = copiedStateLabels
      }
    }

    // copy over sunburst labels
    if ((block.type === 'sunburst') && (this.copiedBlock.type === 'sunburst')) {
      block.props.types = block.props.types || {}
      for (const typeKey in block.props.types) {
        const copiedTypes = this.copiedBlock.props && this.copiedBlock.props.types
        if (copiedTypes && copiedTypes[typeKey]) {
          block.props.types[typeKey] = this.copiedBlock.props.types[typeKey]
        }
      }

      if (this.copiedBlock.props.totalLabel) {
        block.props.totalLabel = this.copiedBlock.props.totalLabel
      }
    }
  }

  clearClipboard () {
    window.localStorage.removeItem(COPIED_BLOCK_KEY)
  }

  // private methods

  copyOverLabels (source = [], target = []) {
    target.forEach((item) => {
      const sourceItem = source.find(({ value }) => (value === item.value))
      if (sourceItem) {
        item.label = sourceItem.label
      }
    })
  }

  copyOverNestedLabels (source = [], target = [], childProp, valueProp, subgroups = []) {
    target.forEach((item) => {
      const sourceItem = source.find((srcItem) => (srcItem[valueProp] === item[valueProp]))
      if (sourceItem) {
        item.label = sourceItem.label
        this.copyOverLabels(sourceItem[childProp], item[childProp])
      } else {
        // look in subgroups for any matches
        const subgroup = subgroups.find(({ value }) => (value === item[valueProp]))
        if (subgroup) {
          item.label = subgroup.label
        }
      }
    })
  }
}

export const blockLabelsCopyService = new BlockLabelsCopyService()
